import { PythonBlockResult } from './types';

export type MatplotlibFlushResult = {
  base64ImageString?: string;
  figureCount: number;
};

/**
 * Selects which output to display in a Python cell, given the single-output UI constraint.
 *
 * **Priority logic:**
 * 1. Visual outputs (html/image) from the last expression take highest priority
 * 2. If no visual output, matplotlib figures (if any) are shown
 * 3. Otherwise, table or primitive outputs from the last expression are shown
 *
 * **Warnings generated:**
 * - When a matplotlib figure is hidden because a visual output took priority
 * - When non-visual output is hidden because a plot took priority
 *
 * @param converted - The result from converting the last Python expression (can be undefined if no expression)
 * @param figFlush - Result from flushing matplotlib figures after execution
 * @returns An object containing the selected result and any warnings to display to the user
 */
export function selectPythonCellOutput(
  converted: PythonBlockResult,
  figFlush: MatplotlibFlushResult | undefined,
): { result: PythonBlockResult; warnings: string[] } {
  const warnings: string[] = [];
  const figureCount = figFlush?.figureCount ?? 0;

  const convertedIsVisual = converted?.type === 'html' || converted?.type === 'image';
  if (convertedIsVisual) {
    if (figureCount > 0) {
      warnings.push(
        'A matplotlib figure was created but not shown due to single-output limitation.',
      );
    }
    return { result: converted, warnings };
  }

  if (figureCount > 0 && figFlush?.base64ImageString !== undefined) {
    if (converted?.type === 'table' || converted?.type === 'primitive') {
      warnings.push(
        'Non-plot output was hidden because a plot was produced; only one output is supported.',
      );
    }
    return {
      result: { type: 'image', base64ImageString: figFlush.base64ImageString },
      warnings,
    };
  }

  return { result: converted, warnings };
}
